import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { keyframes, styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";

import CheckIcon from "@/inline-assets/check.svg";
import { classNames } from "@/util/class-names.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";

import { usePortalContainer } from "../util/use-portal-container.mjs";
import type { SelectValidationProps } from "../util/use-validation-error.mjs";
import useValidationError from "../util/use-validation-error.mjs";

const DropdownWrapper = styled("div")`
  --height: var(--sp-12);
  --padding: var(--sp-3);
  --radius: var(--br);
  --background: var(--shade7);
  --border-size: var(--sp-px);

  --item-padding: var(--sp-3);

  --font-size: 1rem;
  --font-weight: 600;

  &[data-block="true"] {
    flex: 1;
  }

  display: grid;

  user-select: none;

  cursor: pointer;

  select,
  .dropdown {
    grid-area: 1 / 1;

    width: 100%;
    height: var(--height);
  }

  select {
    color: transparent;
    background-color: transparent;

    border: none;
    outline: none;

    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  .dropdown {
    padding: var(--padding);

    color: var(--shade0);
    font-size: var(--font-size);
    font-weight: var(--font-weight);
    line-height: 1.5;

    background-color: var(--background);
    background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M4 6L8 10L12 6H4Z' fill='rgb(153, 156, 162)'/></svg>");
    background-repeat: no-repeat;
    background-position-x: calc(100% - var(--padding));
    background-position-y: center;

    border-radius: var(--radius);
    border: var(--border-size) solid var(--shade6);

    transition: border-color 0.15s;

    &[aria-disabled="true"] {
      color: var(--shade2);

      background-color: var(--shade9);

      cursor: not-allowed;
    }

    &[aria-readonly="true"] {
      cursor: not-allowed;
    }

    .placeholder {
      color: var(--shade2);
      opacity: 75%;
    }
  }

  @media (pointer: coarse) {
    select {
      z-index: 1;
    }
  }

  .dropdown:focus,
  .dropdown:hover,
  select:focus + .dropdown {
    outline: none;

    border-color: var(--shade3);
  }

  &[data-validate="true"] {
    & > select:invalid + .dropdown {
      background-color: rgba(221, 52, 74, 0.15);
      border-color: var(--primary);
    }
  }
`;

const DropdownSelectedLabel = styled("div", React.forwardRef)`
  display: flex;
  align-items: center;
  gap: var(--item-padding);

  margin-right: calc(var(--item-padding) + 16px);

  span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const popIn = () => keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const DropdownMenuContainer = styled("div", React.forwardRef)`
  --padding: var(--sp-2);
  --radius: var(--br);
  --background: var(--shade10);

  --item-gap: var(--sp-1);
  --item-padding: var(--sp-3);
  --item-radius: var(--br);
  --item-background-hover: var(--shade8);

  --font-size: 1rem;
  --font-weight: 600;

  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;

  background-color: var(--background);
  border-radius: var(--radius);
  color: var(--shade0);

  font-family: "Inter", Arial, Helvetica, sans-serif;

  font-size: var(--font-size);
  font-weight: var(--font-weight);

  overflow: hidden;
  box-sizing: border-box;
  pointer-events: all;
  z-index: 10;

  animation: ${popIn} var(--transition);
`;

const DropdownDivider = styled("hr")`
  margin: 0;

  border-color: var(--shade7);
`;

const DropdownSearchInput = styled("input", React.forwardRef)`
  height: var(--sp-12);
  margin: var(--padding);
  padding: var(--item-padding);

  border: 0;
  background-color: var(--shade10);

  transition: border-color 0.15s;

  &::placeholder {
    color: var(--shade2);

    opacity: 75%;
  }

  &:focus {
    outline: none;
  }
`;

const DropdownFooter = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--item-padding);

  flex: 1;
  height: var(--sp-12);
  margin: var(--padding);
  padding: var(--item-padding);

  color: var(--shade2);
`;

const DropdownMenuListContainer = styled("div", React.forwardRef)`
  padding: var(--padding);

  overflow-x: hidden;
  overflow-y: auto;

  user-select: none;

  &:focus {
    outline: none;
  }
`;

const DropdownMenuList = styled("ul")`
  display: flex;
  flex-direction: column;
  gap: var(--item-gap);

  margin: 0;
  padding: 0;
`;

const DropdownMenuListItem = styled("li", React.forwardRef)`
  display: flex;
  align-items: center;
  gap: var(--item-padding);

  padding: var(--item-padding);

  border-radius: var(--item-radius);

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  span {
    flex-grow: 1;
  }

  &[data-selected] svg {
    visibility: visible;
  }

  &:not([aria-disabled="true"]) {
    &:hover,
    &:focus,
    &[data-selected] {
      background-color: var(--item-background-hover);
    }
  }

  &[aria-disabled="true"] {
    color: var(--shade2);
    cursor: not-allowed;

    svg,
    img {
      opacity: 0.5;
    }
  }
`;

const DropdownMenuListIcon = styled("div")`
  transition: opacity 0.2s;

  svg,
  img {
    width: auto;
    max-width: var(--sp-8);
    max-height: var(--sp-6);
  }
`;

const DropdownMenuListSelected = styled(CheckIcon)`
  visibility: hidden;

  width: 1.25rem;
  height: 1.25rem;

  fill: var(--shade0);
`;

type PrimitiveValue = string | number | object;
type DropdownItem<T extends PrimitiveValue> = {
  icon?: React.ReactNode;
  value: T;
  label: string;
  disabled?: boolean;
};

interface BaseDropdownProps<T extends PrimitiveValue> {
  id?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;

  disabled?: boolean;
  readOnly?: boolean;

  search?: boolean;
  maxHeight?: number;
  block?: boolean;

  textClassName?: string;

  items: DropdownItem<T>[];

  onInvalid?: (event: React.FormEvent<HTMLSelectElement>) => void;
}

interface SingleDropdownSelectionProps<T extends PrimitiveValue> {
  multiple?: false;

  value?: T;

  /** A value of `T` indicates a selection, or `undefined` if no value is selected. */
  onChange?: (value: T | undefined) => void;
}

interface MultiDropdownSelectionProps<T extends PrimitiveValue> {
  multiple: true;

  value?: T[];
  onChange?: (value: T[]) => void;
}

type DropdownSelectionProps<T extends PrimitiveValue> =
  | SingleDropdownSelectionProps<T>
  | MultiDropdownSelectionProps<T>;

type DropdownProps<T extends PrimitiveValue> = SelectValidationProps &
  BaseDropdownProps<T> &
  DropdownSelectionProps<T>;

export function Dropdown<T extends PrimitiveValue>({
  id,
  name,
  className,
  placeholder,
  autoComplete,
  required,

  disabled,
  readOnly,

  error,

  search = false,
  maxHeight,
  block,
  textClassName,

  items,

  onInvalid,
  onChange,

  multiple,

  value,
}: DropdownProps<T>) {
  const selected = useMemo(
    () => (Array.isArray(value) ? value : value !== undefined ? [value] : []),
    [value],
  );

  const [isActive, setActive] = useState(false);
  const [shouldShowError, setShouldShowError] = useState(false);

  useEffect(() => {
    if (disabled || readOnly) {
      setActive(false);
    }
  }, [disabled, readOnly]);

  const openDropdown = useCallback(() => {
    if (disabled || readOnly) return;

    setActive(true);
  }, [disabled, readOnly]);

  const closeDropdown = useCallback(() => {
    setActive(false);

    setShouldShowError(true);
  }, []);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const onItemSelected = useCallback(
    (value: T | undefined) => {
      if (multiple === true) {
        if (value === undefined) {
          return;
        }

        const newSelection = [...selected];

        // Toggle the value's selection state
        if (newSelection.includes(value)) {
          newSelection.splice(newSelection.indexOf(value), 1);
        } else {
          newSelection.push(value);
        }

        onChange?.(newSelection);
      } else {
        onChange?.(value);

        setActive(false);
      }

      dropdownRef.current?.focus();

      setShouldShowError(true);
    },
    [multiple, onChange, selected],
  );

  const onValueChanged = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onItemSelected(
        e.target.value ? items[parseInt(e.target.value)].value : undefined,
      );
    },
    [items, onItemSelected],
  );

  const onReset = useCallback(() => {
    if (multiple) {
      onChange?.([]);
    } else {
      onChange?.(undefined);
    }
  }, [multiple, onChange]);

  return (
    <DropdownWrapper
      className={className}
      data-validate={(shouldShowError || !!error) === true ? true : undefined}
      data-block={block?.toString()}
      {...(multiple === true ? { "data-multiple": "" } : {})}
    >
      <NativeSelect
        id={id}
        name={name}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        textClassName={textClassName}
        error={error}
        multiple={multiple ?? false}
        items={items}
        selected={selected}
        onChange={onValueChanged}
        onInvalid={onInvalid}
        setShouldShowError={setShouldShowError}
      />

      <DropdownControl
        role="combobox"
        aria-expanded={isActive}
        aria-controls="dropdown-menu"
        ref={dropdownRef}
        placeholder={placeholder}
        items={items}
        selected={selected}
        onClick={openDropdown}
        disabled={disabled}
        readOnly={readOnly}
        textClassName={textClassName}
      />

      {isActive && (
        <DropdownMenu
          id="dropdown-menu"
          dropdownRef={dropdownRef}
          search={search}
          maxHeight={maxHeight}
          textClassName={textClassName}
          multiple={multiple ?? false}
          items={items}
          selected={selected}
          onItemSelected={onItemSelected}
          onReset={onReset}
          onCancel={closeDropdown}
        />
      )}
    </DropdownWrapper>
  );
}

type NativeSelectProps<T extends PrimitiveValue> = SelectValidationProps & {
  id?: string;
  name?: string;
  autoComplete?: string;
  required?: boolean;

  disabled?: boolean;
  readOnly?: boolean;

  textClassName?: string;

  multiple: boolean;
  items: DropdownItem<T>[];

  selected: T[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onInvalid?: (e: React.FormEvent<HTMLSelectElement>) => void;

  setShouldShowError: (value: boolean) => void;
};

function NativeSelect<T extends PrimitiveValue>({
  id,
  name,
  autoComplete,
  required,

  disabled,
  readOnly,

  textClassName,

  error,

  multiple,
  items,

  selected,
  onChange,
  onInvalid,

  setShouldShowError,

  ...rest
}: NativeSelectProps<T>) {
  const selectedIndex = useMemo(() => {
    if (!multiple) {
      const index = items.findIndex((item) => selected.includes(item.value));

      if (index >= 0) return index;
    }

    return undefined;
  }, [multiple, items, selected]);

  const selectRef = useCallbackRef((element: HTMLSelectElement) => {
    if (!element) return;

    function preventPopup(e: Event) {
      setShouldShowError(true);

      e.preventDefault();
    }

    element.addEventListener("invalid", preventPopup, true);

    return () => {
      element.removeEventListener("invalid", preventPopup, true);
    };
  });

  const errorMessage = useValidationError(selectRef, { error, ...rest });

  return (
    <select
      ref={selectRef}
      name={name}
      autoComplete={autoComplete}
      required={required}
      value={selectedIndex !== undefined ? selectedIndex : ""}
      onChange={onChange}
      onInvalid={onInvalid}
      disabled={disabled}
      aria-readonly={readOnly}
      aria-labelledby={id}
      {...(errorMessage ? { "aria-invalid": "true" } : {})}
      {...classNames(textClassName)}
    >
      {(selectedIndex === undefined || !required) && <option></option>}

      {items.map((item, i) => (
        <option key={`option-${i}`} value={i} disabled={item.disabled}>
          {item.label}
        </option>
      ))}
    </select>
  );
}

type DropdownControlProps<T extends PrimitiveValue> = {
  placeholder?: string;
  items: DropdownItem<T>[];
  selected: T[];

  disabled?: boolean;
  readOnly?: boolean;

  textClassName?: string;
} & React.ComponentPropsWithoutRef<"div">;

function DropdownControlInner<T extends PrimitiveValue>(
  {
    placeholder,
    items,
    selected,
    disabled,
    readOnly,
    textClassName,
    ...props
  }: DropdownControlProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const selectedItems = useMemo<DropdownItem<T>[]>(() => {
    return items.reduce((acc, item) => {
      if (selected.includes(item.value)) {
        acc.push(item);
      }

      return acc;
    }, [] as DropdownItem<T>[]);
  }, [items, selected]);

  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className="dropdown"
      aria-hidden="true"
      aria-disabled={disabled}
      aria-readonly={readOnly}
      {...props}
    >
      {selectedItems.length === 1 ? (
        <DropdownSelectedLabel>
          {selectedItems[0].icon && (
            <DropdownMenuListIcon>{selectedItems[0].icon}</DropdownMenuListIcon>
          )}

          <span {...classNames(textClassName)}>{selectedItems[0].label}</span>
        </DropdownSelectedLabel>
      ) : selectedItems.length > 1 ? (
        <DropdownSelectedLabel>
          <span {...classNames(textClassName)}>
            {t("common:multipleSelected", "{{count}} selected", {
              count: selectedItems.length,
            })}
          </span>
        </DropdownSelectedLabel>
      ) : (
        <div {...classNames("placeholder", textClassName)}>{placeholder}</div>
      )}
    </div>
  );
}

const DropdownControl = React.forwardRef(DropdownControlInner);

type DropdownMenuProps<T extends PrimitiveValue> = {
  id: string;

  dropdownRef: React.RefObject<HTMLElement>;

  search: boolean;
  maxHeight?: number;
  textClassName?: string;

  multiple?: boolean;
  items: DropdownItem<T>[];

  selected: T[];
  onItemSelected: (value: T) => void;
  onReset: () => void;
  onCancel: () => void;
};

export function DropdownMenu<T extends PrimitiveValue>({
  id,

  dropdownRef,

  search,
  maxHeight = 250,
  textClassName,

  multiple,
  items,

  selected,
  onItemSelected,
  onReset,
  onCancel,
}: DropdownMenuProps<T>) {
  const dropdownContainer = usePortalContainer({
    fromElement: dropdownRef,
  });

  const menuContainerRef = useRef<HTMLDivElement>(null);

  // Handles closing the menu when clicked outside of the menu, and key presses
  useEffect(() => {
    if (!menuContainerRef.current) return;

    const element = menuContainerRef.current;

    const handleMouseDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      if (!element.contains(event.target)) {
        onCancel();
      }
    };

    element.ownerDocument.addEventListener("mousedown", handleMouseDown);

    return () => {
      element.ownerDocument.removeEventListener("mousedown", handleMouseDown);
    };
  }, [onCancel]);

  // Ensures that we close the dropdown menu when tabbed beyond the final element
  const handleElementBlur = useCallback(() => {
    setTimeout(() => {
      if (!menuContainerRef.current) return;

      const element = menuContainerRef.current;
      const activeElement =
        menuContainerRef.current.ownerDocument.activeElement;

      if (element && !element.contains(activeElement)) {
        onCancel();
      }
    }, 1);
  }, [onCancel]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollAreaPadding = useCallback((): number => {
    if (!scrollContainerRef.current || items.length === 0) return 0;

    return (
      (scrollContainerRef.current.children[0].children[0] as HTMLLIElement)
        .offsetTop - scrollContainerRef.current.offsetTop
    );
  }, [items.length]);

  const scrollToItem = useCallback(
    (item: number | HTMLLIElement, smooth = false) => {
      if (!scrollContainerRef.current) return;
      if (typeof item === "number") {
        if (item === -1) return;

        item = scrollContainerRef.current.children[0].children[
          item
        ] as HTMLLIElement;
      }

      scrollContainerRef.current.scrollTo({
        top:
          item.offsetTop -
          scrollContainerRef.current.offsetTop -
          scrollAreaPadding(),
        behavior: !smooth ? "auto" : "smooth",
      });

      item.focus({ preventScroll: true });
    },
    [scrollAreaPadding],
  );

  // Filter items
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState<string>("");

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        item.label.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [items, searchText],
  );

  // Scrolls the selected element into view
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const selectedItem =
      !multiple &&
      scrollContainerRef.current.querySelector<HTMLLIElement>(
        "li[data-selected]",
      );

    if (selectedItem) {
      scrollToItem(selectedItem);

      selectedItem.focus({ preventScroll: true });
    } else if (searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      scrollContainerRef.current.focus();
    }
  }, [multiple, scrollToItem]);

  useEffect(() => {
    if (!menuContainerRef.current) return;

    queueMicrotask(() => {
      if (!menuContainerRef.current) return;
      if (!dropdownRef.current) return;

      const containerRect = dropdownContainer.getBoundingClientRect();
      const rect = dropdownRef.current.getBoundingClientRect();

      if (!rect) return;

      const bounds = {
        top: {
          px: `${Math.round(rect.top - containerRect.top - 2)}px`,
          padding: "var(--item-padding) * 0.5",
        },
        left: {
          px: `${Math.round(rect.left - containerRect.left - 1)}px`,
          padding: "var(--item-padding) * 0.5",
        },
        minWidth: `calc(${Math.round(rect.width - 2)}px + var(--item-padding))`,
      };

      const menuStyle = menuContainerRef.current.style;

      menuStyle.top = `calc(${bounds.top.px} - ${bounds.top.padding})`;
      menuStyle.left = `calc(${bounds.left.px} - ${bounds.left.padding})`;
      menuStyle.minWidth = `${bounds.minWidth}`;
    });
  }, [dropdownContainer, dropdownRef]);

  // Jump straight to items on key press, does not filter
  useEffect(() => {
    if (!menuContainerRef.current) return;

    const element = menuContainerRef.current;

    let jumpText = "";

    function jumpToNamedItem(text: string) {
      if (text.length > 0) {
        const index = filteredItems.findIndex((item) =>
          item.label.toLowerCase().startsWith(text),
        );

        // If no item was found, don't scroll, and don't save the text
        if (index === -1) {
          return;
        }

        scrollToItem(index);
      }

      jumpText = text;
    }

    function onKeyDown(e: KeyboardEvent) {
      // Ignore keys when an input field is focused
      if (e.target instanceof HTMLInputElement) return;

      if (e.code === "Backspace") {
        e.preventDefault();

        jumpToNamedItem(jumpText.substring(0, jumpText.length - 1));
      } else if (e.code === "Space") {
        e.preventDefault();
      } else if (e.code === "ArrowDown" || e.code === "ArrowUp") {
        e.preventDefault();

        const activeElement =
          menuContainerRef.current?.ownerDocument.activeElement;

        const nextFocus =
          e.code === "ArrowDown"
            ? activeElement?.nextElementSibling
            : activeElement?.previousElementSibling;

        if (nextFocus instanceof HTMLLIElement) {
          scrollToItem(nextFocus, true);
        }
      }
    }

    function onKeyPressed(e: KeyboardEvent) {
      // Ignore keys when an input field is focused
      if (e.target instanceof HTMLInputElement) return;

      jumpToNamedItem(jumpText + e.key.toLowerCase());
    }

    element.ownerDocument.addEventListener("keydown", onKeyDown);
    element.ownerDocument.addEventListener("keypress", onKeyPressed);

    return () => {
      element.ownerDocument.removeEventListener("keydown", onKeyDown);
      element.ownerDocument.removeEventListener("keypress", onKeyPressed);
    };
  }, [filteredItems, scrollToItem]);

  const { t } = useTranslation();

  return ReactDOM.createPortal(
    <DropdownMenuContainer
      id={id}
      ref={menuContainerRef}
      aria-activedescendant="selected-item"
      aria-autocomplete={search ? "list" : "none"}
      style={{ maxHeight }}
    >
      {search && (
        <>
          <DropdownSearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <DropdownDivider />
        </>
      )}

      <DropdownMenuListContainer ref={scrollContainerRef} tabIndex={0}>
        <DropdownMenuList>
          {filteredItems.map((item, i) => {
            const isSelected = selected.includes(item.value);

            return (
              <DropdownMenuListItemWrapper
                key={`item-${i}`}
                textClassName={textClassName}
                isSelected={isSelected}
                showCheckmark={multiple}
                item={item}
                onSelectItem={onItemSelected}
                onBlur={handleElementBlur}
              />
            );
          })}
        </DropdownMenuList>
      </DropdownMenuListContainer>

      {multiple && (
        <>
          <DropdownDivider />

          <DropdownFooter>
            <span {...classNames(textClassName)}>
              {t("common:multipleSelected", "{{count}} selected", {
                count: selected.length,
              })}
            </span>

            <Button size="small" onClick={onReset}>
              {t("common:reset", "Reset")}
            </Button>
          </DropdownFooter>
        </>
      )}
    </DropdownMenuContainer>,
    dropdownContainer,
  );
}

type DropdownMenuListItemProps<T extends PrimitiveValue> = {
  textClassName?: string;

  isSelected: boolean;
  showCheckmark?: boolean;

  item: DropdownItem<T>;

  onSelectItem: (value: T) => void;
  onBlur: () => void;
};

function DropdownMenuListItemWrapper<T extends PrimitiveValue>({
  textClassName,

  isSelected,
  showCheckmark,

  item,
  onSelectItem,

  onBlur,
}: DropdownMenuListItemProps<T>) {
  const onClick = useCallback(() => {
    if (item.disabled) return;

    onSelectItem(item.value);
  }, [item, onSelectItem]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>) => {
      if (item.disabled) return;

      if (e.key === "Enter") onSelectItem(item.value);
    },
    [item, onSelectItem],
  );

  return (
    <DropdownMenuListItem
      role="button"
      id={isSelected ? "selected-item" : undefined}
      data-selected={isSelected ? "" : undefined}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      aria-disabled={item.disabled}
    >
      {item.icon && <DropdownMenuListIcon>{item.icon}</DropdownMenuListIcon>}

      <span {...classNames(textClassName)}>{item.label}</span>

      {showCheckmark && <DropdownMenuListSelected />}
    </DropdownMenuListItem>
  );
}
